import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import type { Person } from '../../../entities/person.js';
import { ANAusbildung } from '../../entities/employee-sections/an-ausbildung.js';
import { ANBeschaeftigung } from '../../entities/employee-sections/an-beschaeftigung.js';
import { ANDokumente } from '../../entities/employee-sections/an-dokumente.js';
import { ANEinverstaendnisErklaerungen } from '../../entities/employee-sections/an-einverstaendniserklaerungen.js';
import { ANGeringfuegigeBeschaeftigung } from '../../entities/employee-sections/an-geringfuegigebeschaeftigung.js';
import { ANInfos } from '../../entities/employee-sections/an-infos.js';
import { ANPersonalakte } from '../../entities/employee-sections/an-personalakte.js';
import { ANPrivatVersicherung } from '../../entities/employee-sections/an-privatversicherung.js';
import { ANSchwerbehinderung } from '../../entities/employee-sections/an-schwerbehinderung.js';
import { ANSozialVersicherung } from '../../entities/employee-sections/an-sozialversicherung.js';
import { ANSteuerInfos } from '../../entities/employee-sections/an-steuerinfos.js';
import { ANVermoegenswirksameLeistungen } from '../../entities/employee-sections/an-vermoegenswirksameleistungen.js';
import { ANWorkingStudent } from '../../entities/employee-sections/an-working-student.js';
import { UserEmployeeDetailsActions } from '../../UserEmployeeDetailsPolicy.js';
import { EmployeeSectionAusbildungForm } from './EmployeeSectionAusbildungForm.js';
import { EmployeeSectionBeschaeftigungForm } from './EmployeeSectionBeschaeftigungForm.js';
import { EmployeeSectionDokumenteForm } from './EmployeeSectionDokumenteForm.js';
import { EmployeeSectionEinverstaendniserklaerungenForm } from './EmployeeSectionEinverstaendniserklaerungenForm.js';
import { EmployeeSectionGeringfuegigeBeschaeftigungForm } from './EmployeeSectionGeringfuegigeBeschaeftigungForm.js';
import { EmployeeSectionInfosForm } from './EmployeeSectionInfosForm.js';
import { EmployeeSectionPersonalakteForm } from './EmployeeSectionPersonalakteForm.js';
import { EmployeeSectionPrivatVersicherungForm } from './EmployeeSectionPrivatVersicherungForm.js';
import { EmployeeSectionSchwerbehinderungForm } from './EmployeeSectionSchwerbehinderungForm.js';
import { EmployeeSectionSozialVersicherungForm } from './EmployeeSectionSozialVersicherungForm.js';
import { EmployeeSectionSteuernForm } from './EmployeeSectionSteuernForm.js';
import { EmployeeSectionVermoegenswirksameLeistungenForm } from './EmployeeSectionVermoegenswirksameLeistungenForm.js';
import { EmployeeSectionWorkingStudentForm } from './EmployeeSectionWorkingStudentForm.js';

export interface SectionFormDefinition {
  title: string;
  formComponent: (form: FunctionalForm<EntityData<Person>>) => Renderable;
  pathOnMitarbeiterDaten: string;
  ClassType: typeof AdornisEntity;
  viewPermission: UserEmployeeDetailsActions;
}

export const EmployeeSection: SectionFormDefinition[] = [
  {
    title: 'Mitarbeiterinformationen*',
    formComponent: form => EmployeeSectionInfosForm({ form }),
    pathOnMitarbeiterDaten: 'anInfos',
    ClassType: ANInfos,
    viewPermission: UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_INFOS,
  },
  {
    title: 'Steuerinformationen*',
    formComponent: form => EmployeeSectionSteuernForm({ form }),
    pathOnMitarbeiterDaten: 'anSteuerInfos',
    ClassType: ANSteuerInfos,
    viewPermission: UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_STEUERINFOS,
  },
  {
    title: 'Sozialversicherung',
    formComponent: form => EmployeeSectionSozialVersicherungForm({ form }),
    pathOnMitarbeiterDaten: 'anSozialVersicherung',
    ClassType: ANSozialVersicherung,
    viewPermission: UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_SOZIALVERSICHERUNG,
  },
  {
    title: 'Ausbildung',
    formComponent: form => EmployeeSectionAusbildungForm({ form }),
    pathOnMitarbeiterDaten: 'anAusbildung',
    ClassType: ANAusbildung,
    viewPermission: UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_AUSBILDUNG,
  },
  {
    title: 'Werksstudent',
    formComponent: form => EmployeeSectionWorkingStudentForm({ form }),
    pathOnMitarbeiterDaten: 'anWorkingStudent',
    ClassType: ANWorkingStudent,
    viewPermission: UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_WORKING_STUDENT,
  },
  {
    title: 'Für privat versicherte Arbeitnehmer',
    formComponent: form => EmployeeSectionPrivatVersicherungForm({ form }),
    pathOnMitarbeiterDaten: 'anPrivatVersicherung',
    ClassType: ANPrivatVersicherung,
    viewPermission: UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_PRIVATVERSICHERUNG,
  },
  {
    title: 'Für geringfügig Beschäftigte',
    formComponent: form => EmployeeSectionGeringfuegigeBeschaeftigungForm({ form }),
    pathOnMitarbeiterDaten: 'anGeringfuegigeBeschaeftigung',
    ClassType: ANGeringfuegigeBeschaeftigung,
    viewPermission: UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_GERINGFUEGIGE_BESCHAEFTIGUNG,
  },
  {
    title: 'Schwerbehinderung',
    formComponent: form => EmployeeSectionSchwerbehinderungForm({ form }),
    pathOnMitarbeiterDaten: 'anSchwerbehinderung',
    ClassType: ANSchwerbehinderung,
    viewPermission: UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_SCHWERBEHINDERUNG,
  },
  {
    title: 'Vermögenswirksame Leistungen',
    formComponent: form => EmployeeSectionVermoegenswirksameLeistungenForm({ form }),
    pathOnMitarbeiterDaten: 'anVermoegenswirksameLeitungen',
    ClassType: ANVermoegenswirksameLeistungen,
    viewPermission: UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_VERMOEGENSWIRKSAME_LEISTUNGEN,
  },
  {
    title: 'Dokumente',
    formComponent: form => EmployeeSectionDokumenteForm({ form }),
    pathOnMitarbeiterDaten: 'anDokumente',
    ClassType: ANDokumente,
    viewPermission: UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_DOKUMENTE,
  },
  {
    title: 'Personalakte',
    formComponent: form => EmployeeSectionPersonalakteForm({ form }),
    pathOnMitarbeiterDaten: 'anPersonalakte',
    ClassType: ANPersonalakte,
    viewPermission: UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_PERSONALAKTE,
  },
  {
    title: 'Beschäftigung',
    formComponent: form => EmployeeSectionBeschaeftigungForm({ form }),
    pathOnMitarbeiterDaten: 'anBeschaeftigung',
    ClassType: ANBeschaeftigung,
    viewPermission: UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_BESCHAEFTIGUNG,
  },
  {
    title: 'Einverständniserklärungen',
    formComponent: form => EmployeeSectionEinverstaendniserklaerungenForm({ form }),
    pathOnMitarbeiterDaten: 'anEinverstaendnisErklaerungen',
    ClassType: ANEinverstaendnisErklaerungen,
    viewPermission: UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_EINVERSTAENDNISERKLAERUNGEN,
  },
];

export const getMissingEmployeeSections = (person: EntityData<Person>) => {
  return EmployeeSection.filter(section => !person.mitarbeiterDaten?.[section.pathOnMitarbeiterDaten]);
};
